import type { PatientScheduleBlock, ScheduleBlockPrefill } from '@/api/patient-schedule-blocks';
import type { ShiftCalendarHeader } from '@/api/shifts';
import { addDays } from '@/utils/calendar-events';

// Pure helpers that place patient schedule blocks on the calendars. Everything
// works on the wall-clock strings the API ships (YYYY-MM-DD / HH:MM), which
// compare lexically, so the browser time zone never shifts a block.

export interface BlockDaySegment {
  date: string;
  startTime: string;
  endTime: string;
  startsHere: boolean;
  endsHere: boolean;
}

export interface BlockSlotCell {
  slotName: string;
  blockId: number;
  description: string;
}

const FIRST_MINUTE = '00:00';
// vue-cal clamps midnight ends internally; 23:59 keeps a segment inside its day.
const LAST_MINUTE = '23:59';
const DAY_WINDOW_START = '08:00';
const DAY_WINDOW_END = '20:00';
const ISO_DATE_LENGTH = 10;

function isoDate(value: string) {
  return value.slice(0, ISO_DATE_LENGTH);
}

function moment(date: string, time: string) {
  return `${date} ${time}`;
}

function laterOf(a: string, b: string) {
  return a > b ? a : b;
}

function earlierOf(a: string, b: string) {
  return a < b ? a : b;
}

// One segment per calendar date the block touches inside [rangeStart, rangeEnd];
// the first/last dates carry the real times, the middle ones span the whole day.
type BlockRange = Pick<PatientScheduleBlock, 'startDate' | 'startTime' | 'endDate' | 'endTime'>;

// A block ending exactly at midnight owns nothing on its end date.
function segmentFor(block: BlockRange, date: string): BlockDaySegment | null {
  const startsHere = date === block.startDate;
  const endsHere = date === block.endDate;
  const endTime = endsHere ? block.endTime : LAST_MINUTE;
  if (endsHere && endTime === FIRST_MINUTE) return null;

  return { date, startTime: startsHere ? block.startTime : FIRST_MINUTE, endTime, startsHere, endsHere };
}

export function blockDaySegments(block: BlockRange, rangeStart: string, rangeEnd: string): BlockDaySegment[] {
  const segments: BlockDaySegment[] = [];
  const last = earlierOf(block.endDate, rangeEnd);
  for (let date = laterOf(block.startDate, rangeStart); date <= last; date = addDays(date, 1)) {
    const segment = segmentFor(block, date);
    if (segment) segments.push(segment);
  }

  return segments;
}

// Classic grid: paints the block into the day (08:00–20:00) and night
// (20:00–08:00 of the next day) sub-cells of every header it overlaps.
export function expandBlockToSlots(
  block: Pick<PatientScheduleBlock, 'id' | 'description' | 'startDate' | 'startTime' | 'endDate' | 'endTime'>,
  headers: ShiftCalendarHeader[],
): BlockSlotCell[] {
  const blockStart = moment(block.startDate, block.startTime);
  const blockEnd = moment(block.endDate, block.endTime);
  const cells: BlockSlotCell[] = [];

  headers.forEach((header) => {
    const date = isoDate(header.date);
    const windows = [
      {
        child: header.children[0],
        start: moment(date, DAY_WINDOW_START),
        end: moment(date, DAY_WINDOW_END),
      },
      {
        child: header.children[1],
        start: moment(date, DAY_WINDOW_END),
        end: moment(addDays(date, 1), DAY_WINDOW_START),
      },
    ];
    windows.forEach(({ child, start, end }) => {
      if (!child || blockStart >= end || blockEnd <= start) return;

      cells.push({
        slotName: child.value,
        blockId: block.id,
        description: block.description,
      });
    });
  });

  return cells;
}

export function blocksByPatient<T extends { patientId: number }>(blocks: T[]): Map<number, T[]> {
  const map = new Map<number, T[]>();
  blocks.forEach((block) => {
    const list = map.get(block.patientId) ?? [];
    list.push(block);
    map.set(block.patientId, list);
  });

  return map;
}

function humanDate(date: string) {
  const [, month, day] = date.split('-');

  return `${day}/${month}`;
}

// "06/04 09:00 – 08/04 12:00" (same-day blocks: "06/04 09:00 – 12:00").
export function blockPeriodLabel(
  block: Pick<PatientScheduleBlock, 'startDate' | 'startTime' | 'endDate' | 'endTime'>,
): string {
  const start = `${humanDate(block.startDate)} ${block.startTime}`;
  const end = block.startDate === block.endDate ?
    block.endTime :
    `${humanDate(block.endDate)} ${block.endTime}`;

  return `${start} – ${end}`;
}

// Create-dialog prefill from a shift-like slot; an end at or before the start
// (night slots, 23:00 cells) rolls the end date to the next day.
export function blockPrefillForSlot(
  { patientId, startDate, startTime, endTime }: {
    patientId?: number, startDate: string, startTime: string, endTime: string,
  },
): ScheduleBlockPrefill {
  return {
    patientId,
    startDate,
    startTime,
    endDate: endTime <= startTime ? addDays(startDate, 1) : startDate,
    endTime,
  };
}
